/**
 * Briefing History Component
 * Table view of past executive briefings
 * Part 3, Phase 5 - Executive Briefing UI
 *
 * Features:
 * - Table view of past briefings
 * - Filter by date range and type
 * - Search by content
 * - Quick actions (view, export, delete)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Tag,
  Input,
  Select,
  DatePicker,
  Space,
  Popconfirm,
  message,
  Tooltip,
  Empty
} from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import execute from '../../../hooks/execute';
import { protectedResources } from '../../../config/msalConfig';
import { useUser } from '../../../components/UserContext';

const { Option } = Select;
const { RangePicker } = DatePicker;

const BriefingHistory = () => {
  const navigate = useNavigate();
  const { userDetails } = useUser();

  const [loading, setLoading] = useState(false);
  const [briefings, setBriefings] = useState([]);
  const [filteredBriefings, setFilteredBriefings] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState(null);

  /**
   * Fetch briefings
   */
  useEffect(() => {
    if (userDetails?.CustomerID) {
      fetchBriefings();
    }
  }, [userDetails?.CustomerID]);

  const fetchBriefings = async () => {
    try {
      setLoading(true);
      const endpoint = `${protectedResources.api.baseEndPoint}/v1/executive/briefing/list?organizationId=${userDetails.CustomerID}&limit=100`;
      const response = await execute('GET', endpoint);

      if (response.success) {
        const data = response.data || [];
        setBriefings(data);
        setFilteredBriefings(data);
      }
    } catch (err) {
      console.error('[BriefingHistory] Error fetching briefings:', err);
      message.error('Failed to load briefing history');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply filters
   */
  useEffect(() => {
    let filtered = [...briefings];

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.Title?.toLowerCase().includes(searchLower) ||
          b.ExecutiveSummary?.toLowerCase().includes(searchLower)
      );
    }

    // Type filter
    if (filterType) {
      filtered = filtered.filter((b) => b.BriefingType === filterType);
    }

    // Date range filter
    if (filterDateRange && filterDateRange.length === 2) {
      const [start, end] = filterDateRange;
      filtered = filtered.filter((b) => {
        const date = dayjs(b.GeneratedAt);
        return date.isAfter(start) && date.isBefore(end);
      });
    }

    setFilteredBriefings(filtered);
  }, [searchText, filterType, filterDateRange, briefings]);

  /**
   * Handle delete
   */
  const handleDelete = async (briefingId) => {
    try {
      const endpoint = `${protectedResources.api.baseEndPoint}/v1/executive/briefing/${briefingId}`;
      const response = await execute('DELETE', endpoint);

      if (response.success) {
        message.success('Briefing deleted successfully');
        fetchBriefings();
      } else {
        message.error(response.message || 'Failed to delete briefing');
      }
    } catch (err) {
      console.error('[BriefingHistory] Delete failed:', err);
      message.error('Failed to delete briefing');
    }
  };

  /**
   * Handle export
   */
  const handleExport = (briefingId, format = 'PDF') => {
    // Navigate to briefing page where user can export
    navigate(`/executive-briefing/${briefingId}`);
  };

  /**
   * Clear filters
   */
  const clearFilters = () => {
    setSearchText('');
    setFilterType(null);
    setFilterDateRange(null);
  };

  /**
   * Table columns
   */
  const columns = [
    {
      title: 'Title',
      dataIndex: 'Title',
      key: 'Title',
      width: '30%',
      ellipsis: true,
      render: (text, record) => (
        <a
          onClick={() => navigate(`/executive-briefing/${record.BriefingId}`)}
          className="text-[#007ab9] hover:text-[#005a8a] font-medium"
        >
          {text || 'Untitled Briefing'}
        </a>
      )
    },
    {
      title: 'Type',
      dataIndex: 'BriefingType',
      key: 'BriefingType',
      width: '15%',
      filters: [
        { text: 'Weekly', value: 'WEEKLY' },
        { text: 'Monthly', value: 'MONTHLY' },
        { text: 'Quarterly', value: 'QUARTERLY' },
        { text: 'On-Demand', value: 'ON_DEMAND' }
      ],
      onFilter: (value, record) => record.BriefingType === value,
      render: (type) => (
        <Tag color="blue">
          {type?.replace('_', ' ') || 'N/A'}
        </Tag>
      )
    },
    {
      title: 'Health Score',
      dataIndex: 'OverallHealthScore',
      key: 'OverallHealthScore',
      width: '12%',
      sorter: (a, b) => (a.OverallHealthScore || 0) - (b.OverallHealthScore || 0),
      render: (score) => {
        const value = score || 0;
        let color = 'green';
        if (value < 60) color = 'red';
        else if (value < 80) color = 'orange';

        return (
          <Tag color={color} className="font-bold">
            {value}/100
          </Tag>
        );
      }
    },
    {
      title: 'Risk Level',
      dataIndex: 'RiskLevel',
      key: 'RiskLevel',
      width: '12%',
      filters: [
        { text: 'Low', value: 'LOW' },
        { text: 'Medium', value: 'MEDIUM' },
        { text: 'High', value: 'HIGH' },
        { text: 'Critical', value: 'CRITICAL' }
      ],
      onFilter: (value, record) => record.RiskLevel === value,
      render: (level) => {
        const colors = {
          LOW: 'green',
          MEDIUM: 'gold',
          HIGH: 'orange',
          CRITICAL: 'red'
        };
        return <Tag color={colors[level] || 'blue'}>{level || 'N/A'}</Tag>;
      }
    },
    {
      title: 'Modules',
      dataIndex: 'ModuleCount',
      key: 'ModuleCount',
      width: '10%',
      sorter: (a, b) => (a.ModuleCount || 0) - (b.ModuleCount || 0),
      render: (count) => <span className="text-gray-600">{count || 0}</span>
    },
    {
      title: 'Generated',
      dataIndex: 'GeneratedAt',
      key: 'GeneratedAt',
      width: '15%',
      sorter: (a, b) => new Date(a.GeneratedAt) - new Date(b.GeneratedAt),
      defaultSortOrder: 'descend',
      render: (date) => (
        <span className="text-gray-600">
          {dayjs(date).format('MMM DD, YYYY')}
          <div className="text-xs text-gray-400">{dayjs(date).format('h:mm A')}</div>
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Briefing">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/executive-briefing/${record.BriefingId}`)}
            />
          </Tooltip>
          <Tooltip title="Export">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleExport(record.BriefingId)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Briefing"
            description="Are you sure you want to delete this briefing?"
            onConfirm={() => handleDelete(record.BriefingId)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <Input
          placeholder="Search briefings..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />

        <Select
          placeholder="Filter by type"
          value={filterType}
          onChange={setFilterType}
          style={{ width: 200 }}
          allowClear
        >
          <Option value="WEEKLY">Weekly</Option>
          <Option value="MONTHLY">Monthly</Option>
          <Option value="QUARTERLY">Quarterly</Option>
          <Option value="ON_DEMAND">On-Demand</Option>
        </Select>

        <RangePicker
          value={filterDateRange}
          onChange={setFilterDateRange}
          format="YYYY-MM-DD"
          style={{ width: 300 }}
        />

        <Button icon={<FilterOutlined />} onClick={clearFilters}>
          Clear Filters
        </Button>

        <Button icon={<ReloadOutlined />} onClick={fetchBriefings} loading={loading}>
          Refresh
        </Button>

        <div className="ml-auto text-gray-600">
          {filteredBriefings.length} of {briefings.length} briefings
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredBriefings}
        rowKey="BriefingId"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} briefings`
        }}
        locale={{
          emptyText: (
            <Empty
              description="No briefings found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />
    </div>
  );
};

export default BriefingHistory;
